import React, { useState, useEffect } from 'react';
import { Truck, Calendar, DollarSign, Plus, Edit, Trash2, Eye, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../App';

const RentalEquipment = () => {
  const { user } = useAuth();
  const [equipment, setEquipment] = useState([]);
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showRentForm, setShowRentForm] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterVendor, setFilterVendor] = useState('all');
  const [filterFuelType, setFilterFuelType] = useState('all');

  const [newEquipment, setNewEquipment] = useState({
    equipment_name: '',
    equipment_type: '',
    equipment_category: '',
    vendor: '',
    fuel_type: '',
    model: '',
    serial_number: '',
    daily_rate: '',
    weekly_rate: '',
    monthly_rate: '',
    notes: ''
  });

  const [rentalData, setRentalData] = useState({
    project_id: '',
    rental_start_date: '',
    rental_end_date: '',
    rental_rate: '',
    rental_period: 'daily',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      // Fetch equipment
      const equipmentResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rental-equipment`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (equipmentResponse.ok) {
        const equipmentData = await equipmentResponse.json();
        setEquipment(equipmentData);
      }

      // Fetch projects
      const projectsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (projectsResponse.ok) {
        const projectsData = await projectsResponse.json();
        setProjects(projectsData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load rental equipment data');
    }
    setIsLoading(false);
  };

  const handleAddEquipment = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rental-equipment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          ...newEquipment,
          daily_rate: parseFloat(newEquipment.daily_rate),
          weekly_rate: newEquipment.weekly_rate ? parseFloat(newEquipment.weekly_rate) : null,
          monthly_rate: newEquipment.monthly_rate ? parseFloat(newEquipment.monthly_rate) : null
        })
      });

      if (response.ok) {
        toast.success('Equipment added successfully');
        setShowAddForm(false);
        setNewEquipment({
          equipment_name: '',
          equipment_type: '',
          equipment_category: '',
          vendor: '',
          fuel_type: '',
          model: '',
          serial_number: '',
          daily_rate: '',
          weekly_rate: '',
          monthly_rate: '',
          notes: ''
        });
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add equipment');
      }
    } catch (error) {
      console.error('Error adding equipment:', error);
      toast.error('Failed to add equipment');
    }
  };

  const handleRentEquipment = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rental-equipment/${selectedEquipment.id}/rent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          ...rentalData,
          rental_rate: parseFloat(rentalData.rental_rate)
        })
      });

      if (response.ok) {
        toast.success('Equipment rented successfully');
        setShowRentForm(false);
        setRentalData({
          project_id: '',
          rental_start_date: '',
          rental_end_date: '',
          rental_rate: '',
          rental_period: 'daily',
          notes: ''
        });
        setSelectedEquipment(null);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to rent equipment');
      }
    } catch (error) {
      console.error('Error renting equipment:', error);
      toast.error('Failed to rent equipment');
    }
  };

  const handleReturnEquipment = async (equipmentId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rental-equipment/${equipmentId}/return`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        toast.success('Equipment returned successfully');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to return equipment');
      }
    } catch (error) {
      console.error('Error returning equipment:', error);
      toast.error('Failed to return equipment');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      available: { variant: 'default', color: 'bg-green-100 text-green-800' },
      rented: { variant: 'secondary', color: 'bg-blue-100 text-blue-800' },
      maintenance: { variant: 'destructive', color: 'bg-red-100 text-red-800' },
      reserved: { variant: 'outline', color: 'bg-yellow-100 text-yellow-800' }
    };
    const config = statusConfig[status] || statusConfig.available;
    return (
      <Badge className={config.color}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const filteredEquipment = equipment.filter(item => {
    if (filterStatus !== 'all' && item.status !== filterStatus) return false;
    if (filterCategory !== 'all' && item.equipment_category !== filterCategory) return false;
    if (filterVendor !== 'all' && item.vendor !== filterVendor) return false;
    if (filterFuelType !== 'all' && item.fuel_type !== filterFuelType) return false;
    return true;
  });

  const currentRentals = equipment.filter(item => item.status === 'rented');
  const upcomingReservations = equipment.filter(item => item.status === 'reserved');

  const canManage = ['super_admin', 'dispatch', 'admin'].includes(user?.role);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Rental Equipment</h1>
          <p className="text-slate-700 mt-1">Manage equipment rentals and reservations</p>
        </div>
        
        {canManage && (
          <Button onClick={() => setShowAddForm(true)} className="flex items-center">
            <Plus className="h-4 w-4 mr-2" />
            Add Equipment
          </Button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Truck className="h-8 w-8 text-blue-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Equipment</p>
                <p className="text-2xl font-bold text-gray-900">{equipment.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Calendar className="h-8 w-8 text-green-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Available</p>
                <p className="text-2xl font-bold text-gray-900">
                  {equipment.filter(item => item.status === 'available').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <DollarSign className="h-8 w-8 text-blue-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Currently Rented</p>
                <p className="text-2xl font-bold text-gray-900">{currentRentals.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <AlertCircle className="h-8 w-8 text-amber-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Reserved</p>
                <p className="text-2xl font-bold text-gray-900">{upcomingReservations.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="mb-6">
        <div className="flex flex-col space-y-4">
          <div>
            <h3 className="text-sm font-medium text-slate-700 mb-2">Filter by Status</h3>
            <div className="flex space-x-2">
              {['all', 'available', 'rented', 'maintenance', 'reserved'].map(status => (
                <Button
                  key={status}
                  variant={filterStatus === status ? 'default' : 'outline'}
                  onClick={() => setFilterStatus(status)}
                  className="capitalize"
                >
                  {status === 'all' ? 'All Status' : status}
                </Button>
              ))}
            </div>
          </div>
          
          <div>
            <h3 className="text-sm font-medium text-slate-700 mb-2">Filter by Vendor</h3>
            <div className="flex space-x-2">
              {['all', 'Sunbelt'].map(vendor => (
                <Button
                  key={vendor}
                  variant={filterVendor === vendor ? 'default' : 'outline'}
                  onClick={() => setFilterVendor(vendor)}
                  className="capitalize"
                >
                  {vendor === 'all' ? 'All Vendors' : vendor}
                </Button>
              ))}
            </div>
          </div>
          
          <div>
            <h3 className="text-sm font-medium text-slate-700 mb-2">Filter by Equipment Type</h3>
            <div className="flex flex-wrap gap-2">
              {['all', 'Crane', 'Scissor Lift', 'Manlift', 'Forklift', 'Telehandler', 'Material Lift'].map(type => (
                <Button
                  key={type}
                  variant={filterCategory === type ? 'default' : 'outline'}
                  onClick={() => setFilterCategory(type)}
                  className="capitalize"
                >
                  {type === 'all' ? 'All Types' : type}
                </Button>
              ))}
            </div>
          </div>
          
          <div>
            <h3 className="text-sm font-medium text-slate-700 mb-2">Filter by Fuel Type</h3>
            <div className="flex space-x-2">
              {['all', 'Electric', 'Diesel', 'Gas/Propane', 'Manual'].map(fuel => (
                <Button
                  key={fuel}
                  variant={filterFuelType === fuel ? 'default' : 'outline'}
                  onClick={() => setFilterFuelType(fuel)}
                  className="capitalize"
                >
                  {fuel === 'all' ? 'All Fuels' : fuel}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Equipment List */}
      <div className="grid gap-6">
        {filteredEquipment.map(item => (
          <Card key={item.id}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-4">
                    <div>
                      <h3 className="text-lg font-semibold">{item.equipment_name}</h3>
                      <p className="text-gray-600">{item.equipment_type}</p>
                      {item.vendor && (
                        <p className="text-sm text-blue-600 font-medium">Vendor: {item.vendor}</p>
                      )}
                      {item.fuel_type && (
                        <p className="text-sm text-green-600">Fuel: {item.fuel_type}</p>
                      )}
                      {item.equipment_category && (
                        <p className="text-sm text-purple-600">Category: {item.equipment_category}</p>
                      )}
                      {item.model && <p className="text-sm text-gray-500">Model: {item.model}</p>}
                      {item.serial_number && <p className="text-sm text-gray-500">S/N: {item.serial_number}</p>}
                    </div>
                    <div className="ml-6">
                      {getStatusBadge(item.status)}
                    </div>
                  </div>
                  
                  <div className="mt-4 grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Daily Rate</p>
                      <p className="text-lg font-semibold">${item.daily_rate}</p>
                    </div>
                    {item.weekly_rate && (
                      <div>
                        <p className="text-sm font-medium text-gray-600">Weekly Rate</p>
                        <p className="text-lg font-semibold">${item.weekly_rate}</p>
                      </div>
                    )}
                    {item.monthly_rate && (
                      <div>
                        <p className="text-sm font-medium text-gray-600">Monthly Rate</p>
                        <p className="text-lg font-semibold">${item.monthly_rate}</p>
                      </div>
                    )}
                  </div>

                  {item.status === 'rented' && (
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                      <p className="text-sm font-medium text-blue-800">Currently Rented</p>
                      <p className="text-sm text-blue-600">Project: {item.current_project_title}</p>
                      <p className="text-sm text-blue-600">
                        Period: {item.rental_start_date} to {item.rental_end_date}
                      </p>
                      <p className="text-sm text-blue-600">Rented by: {item.rented_by}</p>
                    </div>
                  )}
                </div>

                <div className="flex flex-col space-y-2 ml-6">
                  {canManage && item.status === 'available' && (
                    <Button
                      onClick={() => {
                        setSelectedEquipment(item);
                        setShowRentForm(true);
                      }}
                      className="w-full"
                    >
                      Rent Out
                    </Button>
                  )}
                  
                  {canManage && item.status === 'rented' && (
                    <Button
                      onClick={() => handleReturnEquipment(item.id)}
                      variant="outline"
                      className="w-full"
                    >
                      Mark as Returned
                    </Button>
                  )}
                  
                  {canManage && (
                    <div className="flex space-x-2">
                      <Button variant="outline" size="sm">
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Add Equipment Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Add New Equipment</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAddEquipment} className="space-y-4">
                <div>
                  <Label htmlFor="equipment_name">Equipment Name *</Label>
                  <Input
                    id="equipment_name"
                    value={newEquipment.equipment_name}
                    onChange={(e) => setNewEquipment({...newEquipment, equipment_name: e.target.value})}
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="equipment_type">Equipment Type *</Label>
                  <Input
                    id="equipment_type"
                    value={newEquipment.equipment_type}
                    onChange={(e) => setNewEquipment({...newEquipment, equipment_type: e.target.value})}
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="equipment_category">Category</Label>
                  <select
                    id="equipment_category"
                    value={newEquipment.equipment_category}
                    onChange={(e) => setNewEquipment({...newEquipment, equipment_category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select category</option>
                    <option value="CRANES / BOOM TRUCKS">Cranes / Boom Trucks</option>
                    <option value="ELECTRIC SCISSORLIFTS">Electric Scissor Lifts</option>
                    <option value="MANLIFT ARTICULATING">Manlift Articulating</option>
                    <option value="MANLIFT STRAIGHT BOOM">Manlift Straight Boom</option>
                    <option value="FORKLIFTS - INDUSTRIAL">Forklifts - Industrial</option>
                    <option value="FORKLIFTS - TELEHANDLER">Forklifts - Telehandler</option>
                  </select>
                </div>
                
                <div>
                  <Label htmlFor="vendor">Vendor</Label>
                  <Input
                    id="vendor"
                    value={newEquipment.vendor}
                    onChange={(e) => setNewEquipment({...newEquipment, vendor: e.target.value})}
                    placeholder="e.g., Sunbelt, United Rentals"
                  />
                </div>
                
                <div>
                  <Label htmlFor="fuel_type">Fuel Type</Label>
                  <select
                    id="fuel_type"
                    value={newEquipment.fuel_type}
                    onChange={(e) => setNewEquipment({...newEquipment, fuel_type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select fuel type</option>
                    <option value="Electric">Electric</option>
                    <option value="Diesel">Diesel</option>
                    <option value="Gas/Propane">Gas/Propane</option>
                    <option value="Manual">Manual</option>
                  </select>
                </div>
                
                <div>
                  <Label htmlFor="model">Model</Label>
                  <Input
                    id="model"
                    value={newEquipment.model}
                    onChange={(e) => setNewEquipment({...newEquipment, model: e.target.value})}
                  />
                </div>
                
                <div>
                  <Label htmlFor="daily_rate">Daily Rate *</Label>
                  <Input
                    id="daily_rate"
                    type="number"
                    step="0.01"
                    value={newEquipment.daily_rate}
                    onChange={(e) => setNewEquipment({...newEquipment, daily_rate: e.target.value})}
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="weekly_rate">Weekly Rate</Label>
                  <Input
                    id="weekly_rate"
                    type="number"
                    step="0.01"
                    value={newEquipment.weekly_rate}
                    onChange={(e) => setNewEquipment({...newEquipment, weekly_rate: e.target.value})}
                  />
                </div>
                
                <div className="flex space-x-2">
                  <Button type="submit" className="flex-1">Add Equipment</Button>
                  <Button type="button" variant="outline" onClick={() => setShowAddForm(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Rent Equipment Modal */}
      {showRentForm && selectedEquipment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Rent Equipment: {selectedEquipment.equipment_name}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleRentEquipment} className="space-y-4">
                <div>
                  <Label htmlFor="project_id">Project *</Label>
                  <select
                    id="project_id"
                    value={rentalData.project_id}
                    onChange={(e) => setRentalData({...rentalData, project_id: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select project</option>
                    {projects.map(project => (
                      <option key={project.id} value={project.id}>{project.title}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <Label htmlFor="rental_start_date">Start Date *</Label>
                  <Input
                    id="rental_start_date"
                    type="date"
                    value={rentalData.rental_start_date}
                    onChange={(e) => setRentalData({...rentalData, rental_start_date: e.target.value})}
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="rental_end_date">End Date *</Label>
                  <Input
                    id="rental_end_date"
                    type="date"
                    value={rentalData.rental_end_date}
                    onChange={(e) => setRentalData({...rentalData, rental_end_date: e.target.value})}
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="rental_rate">Rate *</Label>
                  <Input
                    id="rental_rate"
                    type="number"
                    step="0.01"
                    value={rentalData.rental_rate}
                    onChange={(e) => setRentalData({...rentalData, rental_rate: e.target.value})}
                    required
                  />
                </div>
                
                <div className="flex space-x-2">
                  <Button type="submit" className="flex-1">Rent Equipment</Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => {
                      setShowRentForm(false);
                      setSelectedEquipment(null);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default RentalEquipment;